"use client"

import { useEffect, useRef } from "react"
import {
  clearShadowRoot,
  mountHtml,
  mountInlineStyles,
  mountScripts,
  mountStylesheets,
} from "@/components/shared/static-pages/isolate-content.utils"
import type { StaticPageScriptDescriptor } from "@/services/static-page-service/index"

const BASE_SHADOW_STYLE = `
  :host {
    color: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`

interface IsolateContentProps {
  html: string
  stylesheets: string[]
  inlineStyles: string[]
  scripts?: StaticPageScriptDescriptor[]
  allowScriptExecution?: boolean
}

export function StaticPageIsolateContent({
  html,
  stylesheets,
  inlineStyles,
  scripts = [],
  allowScriptExecution = false,
}: IsolateContentProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!hostRef.current) {
      return
    }

    const shadowRoot =
      hostRef.current.shadowRoot ??
      hostRef.current.attachShadow({ mode: "open" })
    const scriptController = new AbortController()

    clearShadowRoot(shadowRoot)

    const baseStyle = shadowRoot.ownerDocument.createElement("style")
    baseStyle.textContent = BASE_SHADOW_STYLE
    shadowRoot.appendChild(baseStyle)

    mountStylesheets(shadowRoot, stylesheets)
    mountInlineStyles(shadowRoot, inlineStyles)
    mountHtml(shadowRoot, html)

    if (allowScriptExecution) {
      mountScripts(shadowRoot, scripts, {
        signal: scriptController.signal,
      })
    }

    return () => {
      scriptController.abort()
      clearShadowRoot(shadowRoot)
    }
  }, [allowScriptExecution, html, inlineStyles, scripts, stylesheets])

  return <div ref={hostRef} />
}
